import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { useChannels } from "@/lib/feature-hooks";
import { toast } from "sonner";
import { Save, Loader2, Hash } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

export default function TempChannelsPanel() {
  const { active } = useServer();
  const channels = useChannels(active?.id, active?.bot_present);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!active?.id || !active.bot_present) return;
    api.get(`/features/temp-channels/${active.id}`).then((r) => setCfg(r.data));
  }, [active?.id, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;
  if (!cfg) return <div className="text-zinc-500 font-mono-tabular text-sm">LOADING…</div>;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/features/temp-channels/${active.id}`, cfg);
      toast.success("Saved");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="temp-channels-panel" className="space-y-8 max-w-3xl">
      <div>
        <div className="overline mb-2"><Hash className="inline w-4 h-4 mr-2 -mt-1" />UTILITIES</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Temporary Channels</h1>
        <p className="text-zinc-400 mt-2">When a member joins the "creator" voice channel, the bot creates a private channel for them.</p>
      </div>

      <div className="tactical-card p-6 space-y-5">
        <button type="button" onClick={() => set("enabled", !cfg.enabled)} className={`w-full p-3 border flex items-center justify-between rounded-lg ${cfg.enabled ? "border-brand-orange bg-brand-orange/5" : "border-brand-line"}`} data-testid="tc-toggle">
          <span className="font-heading uppercase tracking-wider text-sm">Enabled</span>
          <span className={`w-10 h-5 relative rounded-full ${cfg.enabled ? "bg-brand-orange" : "bg-brand-line"}`}>
            <span className={`absolute top-0.5 w-4 h-4 bg-black rounded-full ${cfg.enabled ? "left-5" : "left-0.5"}`} />
          </span>
        </button>
        <div>
          <label className="overline block mb-2">Creator voice channel</label>
          <select className="tactical-input" value={cfg.creator_channel_id || ""} onChange={(e) => set("creator_channel_id", e.target.value || null)} data-testid="tc-creator">
            <option value="">— Select voice channel —</option>
            {channels.filter((c) => c.type === "voice").map((c) => <option key={c.id} value={c.id}>🔊 {c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="overline block mb-2">Channel name template</label>
          <input className="tactical-input" value={cfg.name_template} onChange={(e) => set("name_template", e.target.value)} data-testid="tc-template" />
          <div className="text-[10px] text-zinc-600 font-mono-tabular mt-1">{"{user}"} = display name</div>
        </div>
        <div>
          <label className="overline block mb-2">User limit (0 = unlimited)</label>
          <input type="number" min="0" className="tactical-input" value={cfg.user_limit} onChange={(e) => set("user_limit", parseInt(e.target.value) || 0)} data-testid="tc-limit" />
        </div>
        <div className="flex justify-end pt-2">
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="tc-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
